import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Select from '../../components/ui/Select';
import VideoPlayer from '../../components/ui/VideoPlayer';
import ZoomableVideoPlayer from '../../components/ui/ZoomableVideoPlayer';
import EZOPENPlayer from '../../components/ui/EZOPENPlayer';
import { Accordion, AccordionItem } from '../../components/ui/Accordion';
import { Maximize2, Video, AlertCircle, Minimize2, ChevronLeft, ChevronRight, ArrowUpDown, Grid3x3 } from 'lucide-react';
import CameraArrangeModal from '../../components/cameras/CameraArrangeModal';
import CameraIntercomControl, { cameraIntercomSupport } from '../../components/cameras/CameraIntercomControl';
import { cameraService } from '../../services';
import { useLocation } from '../../contexts/LocationContext';
import type { Camera, CameraStream } from '../../types';
import { waitForEZOPENCleanup } from '../../utils/ezopenLifecycle';
import { FULLSCREEN_CAMERA_LABEL_HEIGHT, getFullscreenGridLayout } from '../../utils/liveViewGrid';

// Helper function to check if camera is online based on protocol_status
const isCameraOnline = (camera: Camera): boolean => {
  // Check protocol_status for any connected protocol
  if (camera.protocol_status && typeof camera.protocol_status === 'object') {
    const statuses = Object.values(camera.protocol_status) as any[];
    // Camera is online if any protocol shows 'connected' status
    const isOnline = statuses.some(status => {
      if (typeof status === 'string') {
        return status === 'connected' || status === 'online';
      }
      if (typeof status === 'object' && status !== null) {
        return status?.status === 'connected' || 
               status?.status === 'online' ||
               status?.connection_status === 'connected' ||
               status?.connected === true;
      }
      return false;
    });
    return isOnline;
  }
  // Fallback to status field if protocol_status is not available
  return camera.status === true;
};

const LiveViewPage: React.FC = () => {
  const { locations, selectedLocationId } = useLocation();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gridSize, setGridSize] = useState<'1' | '4' | '9' | '16'>(() => {
    // Initialize from localStorage, default to '4' if not found
    // Fullscreen mode uses staggered player creation to avoid WebGL context exhaustion
    const saved = localStorage.getItem('liveViewGridSize');
    return (saved === '1' || saved === '4' || saved === '9' || saved === '16') ? saved : '4';
  });
  const [fullscreenCamera, setFullscreenCamera] = useState<Camera | null>(null);
  const [cameraStreams, setCameraStreams] = useState<Record<number, CameraStream>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [groupedCameras, setGroupedCameras] = useState<Record<number, Camera[]>>({});
  const [showArrangeModal, setShowArrangeModal] = useState(false);
  const [isFullscreenGrid, setIsFullscreenGrid] = useState(() => {
    // Restore fullscreen state from localStorage
    const saved = localStorage.getItem('liveViewFullscreenGrid');
    return saved === 'true';
  });
  const [fullscreenPage, setFullscreenPage] = useState(1); // Pagination for fullscreen grid (16 cameras per page)
  const [showPaginationControls, setShowPaginationControls] = useState(true); // Auto-hide pagination controls on inactivity
  const paginationInactivityTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const fetchedLocationsRef = React.useRef<Set<number>>(new Set());
  const cameraRequestRef = useRef(0);

  // Suppress harmless browser video play/pause errors from iframe video streams
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      const message = args[0]?.toString() || '';
      // Suppress "play() request was interrupted" errors - these are harmless reconnection attempts
      if (message.includes('play() request was interrupted') || 
          message.includes('NotAllowedError') ||
          message.includes('AbortError')) {
        return; // Silently ignore
      }
      originalError(...args);
    };
    return () => {
      console.error = originalError;
    };
  }, []);

  // Persist grid size to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('liveViewGridSize', gridSize);
  }, [gridSize]);

  // Refresh only the visible fullscreen page, with cancellation owned by this
  // effect. Cancelling a delay must also settle its promise.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshPhase, setRefreshPhase] = useState<'idle' | 'destroying' | 'batching' | 'complete'>('idle');
  const [visiblePlayerCount, setVisiblePlayerCount] = useState(9999);
  const camerasRef = useRef<Camera[]>([]);
  const refreshCountRef = useRef(0);
  camerasRef.current = cameras;
  const refreshLocation = selectedLocationId === 'all'
    ? undefined
    : locations.find(location => location.ycl_id.toString() === selectedLocationId);
  const autoRefreshEnabled = refreshLocation?.auto_refresh_enabled !== false;
  const configuredInterval = refreshLocation?.auto_refresh_interval_minutes ?? 9.5;
  const refreshIntervalMinutes = Number.isFinite(configuredInterval) && configuredInterval > 0 ? configuredInterval : 9.5;

  useEffect(() => {
    setRefreshPhase('idle');
    setVisiblePlayerCount(9999);
    if (!isFullscreenGrid || !autoRefreshEnabled) return;

    let cancelled = false;
    let refreshing = false;
    const delays = new Map<NodeJS.Timeout, (active: boolean) => void>();
    const wait = (milliseconds: number): Promise<boolean> => new Promise(resolve => {
      const timer = setTimeout(() => {
        delays.delete(timer);
        resolve(!cancelled);
      }, milliseconds);
      delays.set(timer, resolve);
    });

    const interval = setInterval(async () => {
      if (cancelled || refreshing) return;
      const cameraCount = Math.min(16, Math.max(0, camerasRef.current.length - (fullscreenPage - 1) * 16));
      if (cameraCount === 0) return;
      refreshing = true;
      refreshCountRef.current += 1;
      try {
        setRefreshPhase('destroying');
        setVisiblePlayerCount(0);
        // Allow React to unmount the tiles, then await SDK resource cleanup.
        if (!await wait(5000)) return;
        await waitForEZOPENCleanup();
        if (cancelled) return;

        fetchedLocationsRef.current.clear();
        setRefreshKey(key => key + 1);
        setRefreshPhase('batching');
        for (let count = 4; count < cameraCount + 4; count += 4) {
          if (!await wait(300)) return;
          setVisiblePlayerCount(Math.min(count, cameraCount));
        }
        setRefreshPhase('complete');
        // Await this delay so cleanup cannot clear it before it runs.
        if (!await wait(500)) return;
        setRefreshPhase('idle');
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to refresh live players:', error);
          setVisiblePlayerCount(9999);
          setRefreshPhase('idle');
        }
      } finally {
        refreshing = false;
      }
    }, refreshIntervalMinutes * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      delays.forEach((resolve, timer) => {
        clearTimeout(timer);
        resolve(false);
      });
      delays.clear();
    };
  }, [isFullscreenGrid, selectedLocationId, fullscreenPage, autoRefreshEnabled, refreshIntervalMinutes]);

  // Helper function to format camera display name
  const formatCameraName = (camera: Camera): string => {
    const baseName = camera.name || camera.camera_location || 'Camera';
    return `${baseName}-${camera.camera_id}`;
  };

  useEffect(() => {
    // Fetch streams when cameras or location changes
    if (cameras.length > 0) {
      if (selectedLocationId !== 'all') {
        const locId = parseInt(selectedLocationId);
        if (!fetchedLocationsRef.current.has(locId)) {
          fetchBulkStreams(locId);
        }
      } else {
        // When "All Locations" is selected, fetch streams for each unique location
        const uniqueLocations = Array.from(
          new Set(
            cameras
              .map(c => c.location_id)
              .filter((id): id is number => id !== undefined && id !== null)
          )
        );
        uniqueLocations.forEach(locationId => {
          if (!fetchedLocationsRef.current.has(locationId)) {
            fetchBulkStreams(locationId);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras, selectedLocationId, refreshKey]);

  const fetchCameras = useCallback(async () => {
    const request = ++cameraRequestRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const response = selectedLocationId === 'all'
        ? await cameraService.getCameras({ size: 100 })
        : await cameraService.getCamerasByLocation(parseInt(selectedLocationId), { size: 100 });
      if (request === cameraRequestRef.current) setCameras((response.data || []).filter(isCameraOnline));
    } catch (err) {
      if (request === cameraRequestRef.current) setError(err instanceof Error ? err.message : 'Failed to fetch cameras');
    } finally {
      if (request === cameraRequestRef.current) setIsLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    setCameraStreams({});
    setCameras([]);
    setFullscreenCamera(null);
    fetchedLocationsRef.current.clear();
    void fetchCameras();
    return () => { cameraRequestRef.current += 1; };
  }, [fetchCameras]);

  const fetchBulkStreams = async (locationId: number) => {
    try {
      fetchedLocationsRef.current.add(locationId);
      
      // Filter cameras by location
      const camerasInLocation = cameras.filter(c => c.location_id === locationId);
      
      // Separate cloud cameras from NVR/local cameras
      const cloudCameras = camerasInLocation.filter(c => c.cloud_provider);
      const nvrCameras = camerasInLocation.filter(c => !c.cloud_provider);
      
      // Only fetch MediaMTX streams for NON-CLOUD cameras
      if (nvrCameras.length > 0) {
        const streamData = await cameraService.getBulkCameraStreams(locationId, 'hls', 'sub');
        
        setCameraStreams(prev => {
          const streamMap = { ...prev };
          streamData.forEach(stream => {
            streamMap[stream.camera_id] = stream;
          });
          return streamMap;
        });
      }
      
      // Cloud cameras are handled directly by EZOPENPlayer component
      // No need to fetch cloud previews here - EZOPEN uses cloud-tokens endpoint
    } catch (err) {
      console.error(`Failed to fetch bulk streams for location ${locationId}:`, err);
      fetchedLocationsRef.current.delete(locationId);
      
      // Fallback: Fetch individual streams for NON-CLOUD cameras in this location
      const camerasInLocation = cameras.filter(c => c.location_id === locationId);
      const nvrCamerasOnly = camerasInLocation.filter(c => !c.cloud_provider);
      
      for (const camera of nvrCamerasOnly) {
        try {
          await fetchIndividualStream(camera.camera_id, camera);
        } catch (individualErr) {
          console.error(`Failed to fetch stream for camera ${camera.camera_id}:`, individualErr);
        }
      }
    }
  };

  const fetchIndividualStream = async (cameraId: number, camera?: Camera) => {
    try {
      // Skip cloud cameras - EZOPENPlayer handles them via /cloud-tokens endpoint
      const isCloudCamera = camera?.cloud_provider || (camera?.cloud_camera_id && camera.cloud_camera_id.trim() !== '');
      
      if (isCloudCamera) {
        return;
      }
      
      // Use regular NVR stream for non-cloud cameras or as fallback
      const streamResponse = await cameraService.getCameraStream({
        camera_id: cameraId,
        stream_type: 'webrtc',
        stream_quality: 'main',
      });
      
      setCameraStreams(prev => ({
        ...prev,
        [cameraId]: streamResponse
      }));
    } catch (err) {
      console.error(`Failed to fetch stream for camera ${cameraId}:`, err);
    }
  };

  const getGridClass = () => {
    switch (gridSize) {
      case '1': return 'grid-cols-1';
      case '4': return 'grid-cols-1 md:grid-cols-2';
      case '9': return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case '16': return 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
      default: return 'grid-cols-1 md:grid-cols-2';
    }
  };

  const getGridRowHeight = () => {
    switch (gridSize) {
      case '9': return 'minmax(225px, 0.75fr)';
      case '16': return 'minmax(165px, 0.55fr)';
      default: return 'minmax(300px, 1fr)';
    }
  };

  // Pagination logic
  const camerasPerPage = parseInt(gridSize);
  const totalPages = Math.ceil(cameras.length / camerasPerPage);
  const startIndex = (currentPage - 1) * camerasPerPage;
  const endIndex = startIndex + camerasPerPage;
  const displayedCameras = cameras.slice(startIndex, endIndex);
  
  // Adjust currentPage if it exceeds totalPages after gridSize change
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Group cameras by location when "All Locations" is selected
  useEffect(() => {
    if (selectedLocationId === 'all' && cameras.length > 0) {
      const grouped = cameras.reduce((acc, camera) => {
        const locationId = camera.location_id || 0;
        if (!acc[locationId]) {
          acc[locationId] = [];
        }
        acc[locationId].push(camera);
        return acc;
      }, {} as Record<number, Camera[]>);
      setGroupedCameras(grouped);
    } else {
      setGroupedCameras({});
    }
  }, [cameras, selectedLocationId]);

  // Reset to page 1 when location changes (not grid size to avoid stopping playback)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedLocationId]);

  const gridOptions = [
    { label: '1 Camera', value: '1' },
    { label: '4 Cameras', value: '4' },
    { label: '9 Cameras', value: '9' },
    { label: '16 Cameras (Max)', value: '16' },
  ];

  // Render camera card (extracted for reuse in accordion and grid views)
  // Memoized to prevent unnecessary re-renders when grid size changes
  const renderCameraCard = React.useCallback((camera: Camera, index: number = 0) => {
    const streamData = cameraStreams[camera.camera_id];
    // Stagger auto-play by 300ms per camera to prevent WebGL context overload
    const autoPlayDelay = index * 300;
    
    // Build player URL from available stream URLs
    let streamUrl: string | null = null;
    
    if (streamData?.player_url) {
      streamUrl = streamData.player_url;
    } else if (streamData?.web_player_url) {
      streamUrl = streamData.web_player_url;
    } else if (streamData?.stream_url) {
      streamUrl = streamData.stream_url;
    } else if (streamData?.webrtc_url) {
      try {
        const url = new URL(streamData.webrtc_url);
        const cameraSource = url.searchParams.get('src');
        if (cameraSource) {
          streamUrl = `${url.protocol}//${url.host}/stream.html?src=${cameraSource}&mode=webrtc,mse,mp4`;
          if (streamData.stream_token) {
            streamUrl += `&token=${streamData.stream_token}`;
          }
        }
      } catch (e) {
        console.error('Failed to parse webrtc_url:', e);
      }
    }
    
    const isStreamLoading = !streamData;
    
    return (
      <Card 
        key={`${camera.camera_id}-${gridSize}`}
        padding="none" 
        className="overflow-hidden cursor-pointer h-full"
        onClick={() => setFullscreenCamera(camera)}
      >
        <div className="relative w-full h-full bg-black group">
          {camera.cloud_provider ? (
            <div className="absolute inset-0 w-full h-full">
              <EZOPENPlayer
                camera={{
                  id: camera.camera_id,
                  cloud_provider: camera.cloud_provider,
                  capabilities: camera.capabilities,
                  cloud_serial_no: camera.cloud_serial_no,
                  cloud_channel_no: camera.cloud_channel_no
                }}
                mode="live"
                showControls={false}
                showAudioControls={true}
                showIntercom
                playbackTimezone={locations.find(location => location.ycl_id === camera.location_id)?.location_timezone}
                audioControlsSize="sm"
                showAudioWaveform={false}
                autoPlayDelay={autoPlayDelay}
                streamQuality={gridSize === '1' ? 'hd' : 'sub'}
              />
            </div>
          ) : streamUrl ? (
            <div className="absolute inset-0 w-full h-full">
              <VideoPlayer
                src={streamUrl}
                useProxy={false}
                autoPlay
                muted
                controls={false}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              {isStreamLoading ? (
                <>
                  <Video size={48} className="text-text-muted animate-pulse mb-3" />
                  <p className="text-sm text-text-muted font-medium">Loading Stream...</p>
                  <p className="text-xs text-text-muted mt-1">
                    Camera: {camera.ip_address || camera.camera_ip}:{camera.http_port || camera.camera_port}
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle size={48} className="text-yellow-500 mb-3" />
                  <p className="text-sm text-yellow-500 font-medium">Camera Unavailable</p>
                  <p className="text-xs text-text-muted mt-1">
                    Unable to connect to camera
                  </p>
                </>
              )}
            </div>
          )}
          
          {!camera.cloud_provider && <div className="absolute top-2 left-2 z-20"><CameraIntercomControl cameraId={camera.camera_id} cameraName={formatCameraName(camera)} supported={cameraIntercomSupport(camera)} /></div>}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <Button
              variant="primary"
              leftIcon={<Maximize2 size={18} />}
              onClick={(e) => {
                e.stopPropagation();
                setFullscreenCamera(camera);
              }}
            >
              Full Screen
            </Button>
          </div>

          {/* Camera name overlay - bottom left */}
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
            <h3 className="font-medium text-xs text-white truncate">
              {formatCameraName(camera)}
            </h3>
          </div>
        </div>
      </Card>
    );
  }, [cameraStreams, gridSize, setFullscreenCamera, locations]);

  // Handle native browser fullscreen
  const videoContainerRef = React.useRef<HTMLDivElement>(null);
  const [isNativeFullscreen, setIsNativeFullscreen] = React.useState(false);

  const toggleNativeFullscreen = () => {
    if (!videoContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(err => {
        console.error('Error entering fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsNativeFullscreen(document.fullscreenElement === videoContainerRef.current && videoContainerRef.current !== null);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle fullscreen grid mode
  const fullscreenGridRef = React.useRef<HTMLDivElement>(null);
  const [fullscreenViewport, setFullscreenViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));

  useEffect(() => {
    if (!isFullscreenGrid) return;
    const updateViewport = () => setFullscreenViewport({ width: window.innerWidth, height: window.innerHeight });
    updateViewport();
    window.addEventListener('resize', updateViewport);
    document.addEventListener('fullscreenchange', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      document.removeEventListener('fullscreenchange', updateViewport);
    };
  }, [isFullscreenGrid]);
  
  const toggleFullscreenGrid = async () => {
    if (!isFullscreenGrid) {
      setIsFullscreenGrid(true);
      localStorage.setItem('liveViewFullscreenGrid', 'true');
      // Enter browser fullscreen after state update
      await new Promise(resolve => setTimeout(resolve, 100));
      if (fullscreenGridRef.current && !document.fullscreenElement) {
        try {
          await fullscreenGridRef.current.requestFullscreen();
        } catch (err) {
          console.error('Error entering fullscreen:', err);
        }
      }
    } else {
      setIsFullscreenGrid(false);
      localStorage.removeItem('liveViewFullscreenGrid');
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  };

  // Reset fullscreen page and refresh counter when entering fullscreen or changing location
  React.useEffect(() => {
    setFullscreenPage(1);
    if (isFullscreenGrid) {
      refreshCountRef.current = 0; // Reset counter on new fullscreen session
      console.log('🎬 Fullscreen session started - refresh counter reset');
    }
  }, [isFullscreenGrid, selectedLocationId]);

  // Auto-hide pagination controls after 3 seconds of mouse inactivity in fullscreen
  const resetPaginationInactivityTimer = React.useCallback(() => {
    if (!isFullscreenGrid) return;

    // Show controls when mouse moves
    setShowPaginationControls(true);

    // Clear existing timer
    if (paginationInactivityTimerRef.current) {
      clearTimeout(paginationInactivityTimerRef.current);
    }

    // Hide controls after 3 seconds of inactivity
    paginationInactivityTimerRef.current = setTimeout(() => {
      setShowPaginationControls(false);
    }, 3000);
  }, [isFullscreenGrid]);

  // Cleanup pagination inactivity timer on unmount
  React.useEffect(() => {
    return () => {
      if (paginationInactivityTimerRef.current) {
        clearTimeout(paginationInactivityTimerRef.current);
      }
    };
  }, []);

  // Exit fullscreen grid when ESC is pressed
  React.useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreenGrid) {
        setIsFullscreenGrid(false);
        localStorage.removeItem('liveViewFullscreenGrid');
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isFullscreenGrid]);

  // Auto-enter browser fullscreen on mount if fullscreen state was restored from localStorage
  React.useEffect(() => {
    if (isFullscreenGrid && fullscreenGridRef.current && !document.fullscreenElement) {
      const enterFullscreen = async () => {
        try {
          await fullscreenGridRef.current?.requestFullscreen();
          console.log('📺 Auto-restored fullscreen mode after page reload');
        } catch (err) {
          console.error('Error auto-entering fullscreen:', err);
        }
      };
      // Small delay to ensure DOM is ready
      const timer = setTimeout(enterFullscreen, 300);
      return () => clearTimeout(timer);
    }
  }, []); // Empty deps - only run once on mount

  // Render fullscreen grid (dynamic layout based on camera count)
  if (isFullscreenGrid) {
    // WebGL context limit: Browsers have a hard limit of 16 WebGL contexts
    // EZOPEN SDK uses WebGL for rendering, so we can only show 16 cameras max
    const WEBGL_CONTEXT_LIMIT = 16;

    // Calculate pagination for fullscreen (16 cameras per page)
    const totalCameras = cameras.length;
    const totalPages = Math.ceil(totalCameras / WEBGL_CONTEXT_LIMIT);
    const startIndex = (fullscreenPage - 1) * WEBGL_CONTEXT_LIMIT;
    const endIndex = startIndex + WEBGL_CONTEXT_LIMIT;

    // In fullscreen, show current page of cameras (max 16 per page due to WebGL limit)
    const fullscreenCameras = cameras.slice(startIndex, endIndex);
    const displayedCount = fullscreenCameras.length;
    const hasMultiplePages = totalPages > 1;
    const gridLayout = getFullscreenGridLayout(displayedCount, fullscreenViewport.width, fullscreenViewport.height);
    
    return (
      <div 
        ref={fullscreenGridRef}
        className="fixed inset-0 bg-black z-50 overflow-hidden"
        style={{ margin: 0, padding: 0, cursor: showPaginationControls ? 'default' : 'none' }}
        onMouseMove={resetPaginationInactivityTimer}
        onMouseEnter={resetPaginationInactivityTimer}
      >
        {/* Exit button - Auto-hides after 3s of inactivity */}
        <button
          onClick={toggleFullscreenGrid}
          className={`absolute top-4 right-4 z-50 bg-black/70 hover:bg-black/90 text-white rounded-full p-3 border-2 border-white/30 transition-all duration-300 ${
            showPaginationControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          title="Exit Fullscreen Grid"
        >
          <Minimize2 size={24} />
        </button>

        {/* Refresh status indicator */}
        {refreshPhase !== 'idle' && (
          <div className="absolute top-4 left-4 z-50 bg-blue-600/90 text-white px-4 py-2 rounded-lg text-sm font-medium">
            {refreshPhase === 'destroying' && '🧹 Cleaning up WebGL contexts...'}
            {refreshPhase === 'batching' && `📹 Loading cameras: ${visiblePlayerCount}/${totalCameras}`}
            {refreshPhase === 'complete' && '✅ Refresh complete'}
          </div>
        )}

        {/* Pagination info for multiple pages - Auto-hides after 3s of inactivity */}
        {hasMultiplePages && (
          <div
            className={`absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-blue-600/90 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg flex items-center gap-3 transition-all duration-300 ${
              showPaginationControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
            }`}
          >
            <button
              onClick={() => setFullscreenPage(p => Math.max(1, p - 1))}
              disabled={fullscreenPage === 1}
              className="px-2 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
            >
              ←
            </button>
            <span>
              Page {fullscreenPage} of {totalPages} ({startIndex + 1}-{startIndex + displayedCount} of {totalCameras} cameras)
            </span>
            <button
              onClick={() => setFullscreenPage(p => Math.min(totalPages, p + 1))}
              disabled={fullscreenPage === totalPages}
              className="px-2 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
            >
              →
            </button>
          </div>
        )}

        {/* Fill the viewport and expand incomplete rows without leaving empty slots. */}
        <div 
          className="grid w-full h-full"
          style={{ 
            gap: 0, 
            margin: 0, 
            padding: 0,
            gridTemplateColumns: `repeat(${gridLayout.columns}, minmax(0, 1fr))`,
            gridTemplateRows: gridLayout.rowHeights.map(height => `${height}px`).join(' ')
          }}
        >
          {fullscreenCameras.map((camera, index) => {
            // Show placeholder during refresh if this camera hasn't been created yet
            const shouldShowPlayer = refreshPhase === 'idle' || index < visiblePlayerCount;
            const streamData = cameraStreams[camera.camera_id];
            const autoPlayDelay = index * 300;
            
            let streamUrl: string | null = null;
            
            if (streamData?.player_url) {
              streamUrl = streamData.player_url;
            } else if (streamData?.web_player_url) {
              streamUrl = streamData.web_player_url;
            } else if (streamData?.stream_url) {
              streamUrl = streamData.stream_url;
            } else if (streamData?.webrtc_url) {
              try {
                const url = new URL(streamData.webrtc_url);
                const cameraSource = url.searchParams.get('src');
                if (cameraSource) {
                  streamUrl = `${url.protocol}//${url.host}/stream.html?src=${cameraSource}&mode=webrtc,mse,mp4`;
                  if (streamData.stream_token) {
                    streamUrl += `&token=${streamData.stream_token}`;
                  }
                }
              } catch (e) {
                console.error('Failed to parse webrtc_url:', e);
              }
            }
            
            return (
              <div 
                key={`camera-slot-${index}-${camera.camera_id}`}
                className="relative flex flex-col w-full h-full min-w-0 min-h-0 bg-black overflow-hidden"
                style={{ margin: 0, padding: 0, border: 'none',
                  gridRow: gridLayout.cells[index].row,
                  gridColumn: `span ${gridLayout.cells[index].columnSpan}` }}
              >
                <div className="relative flex-1 min-h-0 w-full">
                  {/* Placeholder during refresh */}
                  {!shouldShowPlayer && (
                    <div className="absolute inset-0 flex items-center justify-center text-white/50">
                      <div className="text-center">
                        <div className="animate-pulse mb-2">⏳</div>
                        <p className="text-xs">Loading...</p>
                      </div>
                    </div>
                  )}
                
                  {/* Camera player - only mount when ready */}
                  {shouldShowPlayer && (
                    <>
                      {camera.cloud_provider ? (
                        <div className="absolute inset-0 w-full h-full">
                          <EZOPENPlayer
                            key={`ezopen-${camera.camera_id}-${refreshKey}`}
                            camera={{
                              id: camera.camera_id,
                              cloud_provider: camera.cloud_provider,
                              cloud_serial_no: camera.cloud_serial_no,
                              cloud_channel_no: camera.cloud_channel_no
                            }}
                            mode="live"
                            showControls={false}
                            showAudioControls={false}
                            playbackTimezone={locations.find(location => location.ycl_id === camera.location_id)?.location_timezone}
                            showAudioWaveform={false}
                            autoPlayDelay={autoPlayDelay}
                            fillContainer={true}
                            videoFit="fill"
                            streamQuality={displayedCount === 1 ? 'hd' : 'sub'}
                          />
                        </div>
                      ) : streamUrl ? (
                        <div className="absolute inset-0 w-full h-full">
                          <VideoPlayer
                            key={`video-${camera.camera_id}-${refreshKey}`}
                            src={streamUrl}
                            useProxy={false}
                            autoPlay
                            muted
                            controls={false}
                            className="w-full h-full object-fill"
                          />
                        </div>
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
                          <Video size={32} className="text-gray-600 animate-pulse mb-2" />
                          <p className="text-xs text-gray-400">Loading...</p>
                        </div>
                      )}
                    </>
                  )}

                </div>

                {/* Keep the label below the picture so it cannot obscure the feed. */}
                <div className="flex shrink-0 items-center px-2 bg-gray-950" style={{ height: FULLSCREEN_CAMERA_LABEL_HEIGHT }}>
                  <p className="text-white text-xs truncate font-medium">
                    {formatCameraName(camera)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (fullscreenCamera) {
    const streamData = cameraStreams[fullscreenCamera.camera_id];
    
    // Build player URL from available stream URLs
    // Priority: player_url > web_player_url > stream_url (cloud) > construct from webrtc_url
    let streamUrl: string | null = null;
    
    if (streamData?.player_url) {
      streamUrl = streamData.player_url;
    } else if (streamData?.web_player_url) {
      streamUrl = streamData.web_player_url;
    } else if (streamData?.stream_url) {
      // Direct stream URL (cloud preview or custom stream)
      streamUrl = streamData.stream_url;
    } else if (streamData?.webrtc_url) {
      try {
        const url = new URL(streamData.webrtc_url);
        const cameraSource = url.searchParams.get('src');
        if (cameraSource) {
          streamUrl = `${url.protocol}//${url.host}/stream.html?src=${cameraSource}&mode=webrtc,mse,mp4`;
          if (streamData.stream_token) {
            streamUrl += `&token=${streamData.stream_token}`;
          }
        }
      } catch (e) {
        console.error('Failed to parse webrtc_url:', e);
      }
    }
    
    const isStreamLoading = !streamData;
    
    return (
      <MainLayout fullHeight>
        <div className="flex flex-col h-full min-h-0 overflow-hidden gap-3">
          {/* Header - Fixed height, compact */}
          <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Button
                variant="outline"
                leftIcon={<Minimize2 size={18} />}
                onClick={() => setFullscreenCamera(null)}
              >
                Back to Grid
              </Button>
              <div>
                <h2 className="text-xl font-semibold text-text-primary">
                  {formatCameraName(fullscreenCamera)}
                </h2>
                <p className="text-sm text-text-muted">
                  {fullscreenCamera.ip_address || fullscreenCamera.camera_ip}:{fullscreenCamera.http_port || fullscreenCamera.camera_port}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                leftIcon={isNativeFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                onClick={toggleNativeFullscreen}
              >
                {isNativeFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </Button>
              <Badge variant={(streamUrl || fullscreenCamera.cloud_provider) ? "success" : "warning"} dot>
                {(streamUrl || fullscreenCamera.cloud_provider) ? 'Live' : 'Connecting...'}
              </Badge>
            </div>
          </div>
          {/* Video container - Flexible, fills remaining space */}
          <div
            ref={videoContainerRef}
            className="relative w-full flex-1 flex items-center justify-center overflow-hidden min-h-0 bg-black rounded-card"
            style={isNativeFullscreen ? { width: '100vw', height: '100vh', flex: 'none' } : undefined}
          >
            <Card padding="none" className="absolute inset-0 bg-black w-full h-full overflow-hidden">
              {fullscreenCamera.cloud_provider ? (
                // Cloud camera - use EZOPEN plugin
                <EZOPENPlayer
                  camera={{
                    id: fullscreenCamera.camera_id,
                    cloud_provider: fullscreenCamera.cloud_provider,
                    capabilities: fullscreenCamera.capabilities,
                    cloud_serial_no: fullscreenCamera.cloud_serial_no,
                    cloud_channel_no: fullscreenCamera.cloud_channel_no
                  }}
                  mode="live"
                  showControls={true}
                  showAudioControls={true}
                  showIntercom
                  playbackTimezone={locations.find(location => location.ycl_id === fullscreenCamera.location_id)?.location_timezone}
                  audioControlsSize="lg"
                  showAudioWaveform={true}
                  fillContainer={true}
                  cameraName={formatCameraName(fullscreenCamera)}
                  onToggleFullscreen={toggleNativeFullscreen}
                />
              ) : streamUrl ? (
                // NVR camera - use zoomable video player
                <ZoomableVideoPlayer
                  src={streamUrl}
                  className="absolute inset-0"
                  showControls={true}
                />
              ) : (
              <div className="text-center space-y-4">
                {isStreamLoading ? (
                  <>
                    <Video size={64} className="text-text-muted mx-auto animate-pulse" />
                    <div className="text-text-muted">
                      <p className="text-lg font-medium">Loading Stream...</p>
                      <p className="text-sm">Camera: {fullscreenCamera.ip_address || fullscreenCamera.camera_ip}:{fullscreenCamera.http_port || fullscreenCamera.camera_port}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle size={64} className="text-yellow-500 mx-auto" />
                    <div className="text-text-muted">
                      <p className="text-lg font-medium text-yellow-500">Camera Unavailable</p>
                      <p className="text-sm">Unable to connect to camera at {fullscreenCamera.ip_address || fullscreenCamera.camera_ip}:{fullscreenCamera.http_port || fullscreenCamera.camera_port}</p>
                      <p className="text-xs mt-2">The camera may be offline or unreachable</p>
                    </div>
                  </>
                )}
              </div>
            )}
            {!fullscreenCamera.cloud_provider && <div className="absolute top-3 left-3 z-20"><CameraIntercomControl cameraId={fullscreenCamera.camera_id} cameraName={formatCameraName(fullscreenCamera)} supported={cameraIntercomSupport(fullscreenCamera)} size="lg" /></div>}
            </Card>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout fullHeight>
      <div className="flex flex-col h-full min-h-0 overflow-hidden gap-4">
        {/* Header Section - Compact */}
        <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
          <div>
            <h1 className="page-title">Live View</h1>
            <p className="page-description">Real-time camera monitoring</p>
          </div>
          <Badge variant="success" dot size="md">
            {cameras.length} Cameras Online
          </Badge>
        </div>

        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}

        {/* Controls - Compact */}
        <Card padding="sm" className="flex-shrink-0">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label="Camera grid layout"
              className="min-w-[150px]"
              options={gridOptions}
              value={gridSize}
              onChange={(e) => setGridSize(e.target.value as any)}
            />
            <Button
              variant="primary"
              size="md"
              leftIcon={<Grid3x3 size={16} />}
              onClick={toggleFullscreenGrid}
              title="View all cameras in fullscreen 4x4 grid"
              disabled={cameras.length === 0}
            >
              Grid View
            </Button>
            {selectedLocationId !== 'all' && cameras.length > 0 && (
              <Button
                variant="outline"
                size="md"
                leftIcon={<ArrowUpDown size={16} />}
                onClick={() => setShowArrangeModal(true)}
                title="Arrange camera display order"
              >
                Arrange
              </Button>
            )}
          </div>
        </Card>

        {/* Camera Grid - Takes remaining space */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loading size="lg" text="Loading cameras..." />
            </div>
          ) : cameras.length === 0 ? (
            <Card padding="lg" className="h-full">
              <div className="text-center flex flex-col items-center justify-center h-full">
                <AlertCircle size={48} className="text-text-muted mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">No Online Cameras</h3>
                <p className="text-text-muted">All cameras are currently offline or no cameras found.</p>
              </div>
            </Card>
          ) : selectedLocationId === 'all' && Object.keys(groupedCameras).length > 0 ? (
            <div className="h-full overflow-y-auto">
              <Accordion>
            {Object.entries(groupedCameras).map(([locationId, locationCameras]) => {
              const location = locations.find(loc => loc.ycl_id === parseInt(locationId));
              const locationName = location?.ycl_name || `Location ${locationId}`;
              const camerasPerPage = parseInt(gridSize);
              const displayedLocationCameras = locationCameras.slice(0, camerasPerPage);
              
              return (
                <AccordionItem
                  key={locationId}
                  title={locationName}
                  subtitle={`${locationCameras.length} camera${locationCameras.length !== 1 ? 's' : ''}`}
                  badge={
                    <Badge variant="success" size="sm">
                      {locationCameras.length} Online
                    </Badge>
                  }
                  defaultOpen={false}
                >
                  <div className={`grid ${getGridClass()} gap-4`} style={{ gridAutoRows: getGridRowHeight() }}>
                    {displayedLocationCameras.map((camera, idx) => renderCameraCard(camera, idx))}
                  </div>
                  {locationCameras.length > camerasPerPage && (
                    <div className="mt-4">
                      <Alert
                        variant="info"
                        message={`Showing ${displayedLocationCameras.length} of ${locationCameras.length} cameras. ${camerasPerPage < 16 ? 'Increase grid size or use' : 'Use'} pagination below to view more. (Max 16 cameras per page due to browser limitations)`}
                      />
                    </div>
                  )}
                </AccordionItem>
              );
            })}
              </Accordion>
            </div>
          ) : (
            <div className="h-full flex flex-col overflow-hidden">
              {/* Grid - Takes available space */}
              <div className="flex-1 overflow-auto">
                <div className={`grid ${getGridClass()} gap-4`} style={{ gridAutoRows: getGridRowHeight() }}>
                  {displayedCameras.map((camera, idx) => renderCameraCard(camera, idx))}
                </div>
              </div>

              {/* Pagination Controls - Fixed at bottom */}
              {totalPages > 1 && (
                <Card padding="sm" className="flex-shrink-0 mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-text-muted">
                    Page {currentPage} of {totalPages} ({cameras.length} total cameras)
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<ChevronLeft size={16} />}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <div className="hidden sm:flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 rounded-button text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-accent-primary text-white'
                                : 'bg-surface-secondary text-text-secondary hover:bg-surface-hover'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      rightIcon={<ChevronRight size={16} />}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Camera Arrange Modal */}
      {selectedLocationId !== 'all' && (
        <CameraArrangeModal
          isOpen={showArrangeModal}
          onClose={() => setShowArrangeModal(false)}
          cameras={cameras}
          locationId={parseInt(selectedLocationId)}
          locationName={locations.find(loc => loc.ycl_id === parseInt(selectedLocationId))?.ycl_name}
          onSuccess={() => {
            // Refresh cameras after order update
            fetchCameras();
          }}
        />
      )}
    </MainLayout>
  );
};

export default LiveViewPage;
